import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Exercise {
  id?: string;
  name: string;
  muscle_groups: string[];
  equipment?: string;
  sets?: number;
  reps?: number;
  weight_kg?: number;
  distance_km?: number;
  duration_seconds?: number;
  rest_seconds?: number;
  notes?: string;
  category?: 'Strength' | 'Cardio';
  set_details?: Array<{
    set_number: number;
    reps: number | null;
    weight_kg: number | null;
    completed?: boolean;
    drop_set?: boolean;
    isDropSet?: boolean;
    isWeightPB?: boolean;
    isRepsPB?: boolean;
    isVolumePB?: boolean;
  }>;
}

export interface Workout {
  id?: string;
  user_id?: string;
  title: string;
  description?: string;
  workout_type?: string;
  duration_minutes?: number;
  calories_burned?: number;
  difficulty_level?: string;
  notes?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
  exercises?: Exercise[];
}

export const useWorkouts = () => {
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const refetchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Initial fetch and real-time subscription
  useEffect(() => {
    if (user) {
      fetchWorkouts();

      // IMPORTANT: channel names are global in supabase-js.
      // If multiple components use this hook (e.g. Workouts page + EditWorkoutModal),
      // a shared channel name can be removed by one component and break realtime for the rest.
      const channelInstanceId = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      const scheduleFetchWorkouts = () => {
        if (refetchDebounceRef.current) clearTimeout(refetchDebounceRef.current);
        refetchDebounceRef.current = setTimeout(() => {
          fetchWorkouts();
        }, 400);
      };

      // Subscribe to real-time changes for workouts and exercises
      const workoutsChannel = supabase
        .channel(`workouts-realtime-${user.id}-${channelInstanceId}`)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'workouts',
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            scheduleFetchWorkouts();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'exercises',
          },
          () => {
            scheduleFetchWorkouts();
          }
        )
        .subscribe();

      return () => {
        if (refetchDebounceRef.current) {
          clearTimeout(refetchDebounceRef.current);
          refetchDebounceRef.current = null;
        }
        supabase.removeChannel(workoutsChannel);
      };
    } else {
      setWorkouts([]);
      setLoading(false);
    }
  }, [user]);

  const fetchWorkouts = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          *,
          exercises (*)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Cast the category and set_details fields to proper types
      const workoutsWithTypedExercises = (data || []).map(workout => ({
        ...workout,
        exercises: workout.exercises?.map(exercise => ({
          ...exercise,
          category: (exercise.category as 'Strength' | 'Cardio') || 'Strength',
          set_details: exercise.set_details as Exercise['set_details']
        })) || []
      }));
      
      setWorkouts(workoutsWithTypedExercises);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWorkout = async (workoutData: Omit<Workout, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: 'No user found' };

    try {
      const { data, error } = await supabase
        .from('workouts')
        .insert([{ ...workoutData, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      
      // Refresh workouts in background (non-blocking) to speed up save
      fetchWorkouts().catch(err => console.warn('Background fetchWorkouts failed:', err));
      return { data, error: null };
    } catch (error) {
      console.error('Error creating workout:', error);
      return { error };
    }
  };

  const addExerciseToWorkout = async (
    workoutId: string,
    exercise: Omit<Exercise, 'id'>,
    options?: { skipRefresh?: boolean }
  ) => {
    if (!user) return { error: 'No user found' };

    try {
      // Ensure cardio exercises are saved as duration-only
      const isCardio = exercise.category === 'Cardio';
      const payload: any = {
        workout_id: workoutId,
        name: exercise.name,
        muscle_groups: exercise.muscle_groups ?? null,
        equipment: exercise.equipment ?? null,
        notes: exercise.notes ?? null,
        category: exercise.category || 'Strength',
        // Duration and distance for cardio, otherwise null
        duration_seconds: isCardio ? (exercise.duration_seconds ?? null) : null,
        distance_km: isCardio ? (exercise.distance_km ?? null) : null,
        // Strength fields only when not cardio
        sets: isCardio ? null : (exercise.sets ?? null),
        reps: isCardio ? null : (exercise.reps ?? null),
        weight_kg: isCardio ? null : (exercise.weight_kg ?? null),
        rest_seconds: isCardio ? null : (exercise.rest_seconds ?? null),
        // set_details stores all sets as JSON array (new format)
        set_details: exercise.set_details ?? null,
      };

      const { data, error } = await supabase
        .from('exercises')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      // Refresh workouts in background (non-blocking) to speed up save
      if (!options?.skipRefresh) {
        fetchWorkouts().catch(err => console.warn('Background fetchWorkouts failed:', err));
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error adding exercise:', error);
      return { error };
    }
  };

  const completeWorkout = async (workoutId: string, completionData?: { duration_minutes?: number; calories_burned?: number; notes?: string }) => {
    if (!user) return { error: 'No user found' };

    try {
      const { error } = await supabase
        .from('workouts')
        .update({ 
          completed_at: new Date().toISOString(),
          ...completionData 
        })
        .eq('id', workoutId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      await fetchWorkouts(); // Refresh workouts
      return { error: null };
    } catch (error) {
      console.error('Error completing workout:', error);
      return { error };
    }
  };

  const getRecentWorkouts = (limit: number = 5) => {
    return workouts
      .filter(w => w.completed_at)
      .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())
      .slice(0, limit);
  };

  const getWorkoutStats = () => {
    const completedWorkouts = workouts.filter(w => w.completed_at);
    const totalWorkouts = completedWorkouts.length;
    const totalTime = completedWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0);
    const avgDuration = totalWorkouts > 0 ? Math.round(totalTime / totalWorkouts) : 0;
    
    // Calculate weekly average (last 4 weeks)
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
    
    const recentWorkouts = completedWorkouts.filter(w => 
      new Date(w.completed_at!) >= fourWeeksAgo
    );
    const weeklyAverage = recentWorkouts.length / 4;
    
    // Calculate monthly data (last 6 months)
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date();
      monthStart.setMonth(monthStart.getMonth() - i);
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0);
      monthEnd.setHours(23, 59, 59, 999);
      
      const monthWorkouts = completedWorkouts.filter(w => {
        const workoutDate = new Date(w.completed_at!);
        return workoutDate >= monthStart && workoutDate <= monthEnd;
      });
      
      monthlyData.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short' }),
        workouts: monthWorkouts.length
      });
    }

    return {
      totalWorkouts,
      totalTime,
      avgDuration,
      weeklyAverage: Math.round(weeklyAverage * 10) / 10,
      monthlyData,
      lastWorkout: completedWorkouts[0]?.completed_at
    };
  };

  const getStrengthProgress = () => {
    const strengthWorkouts = workouts.filter(w => 
      w.completed_at && (w.workout_type === 'strength' || w.workout_type === 'gym' || !w.workout_type)
    );

    const exerciseProgress = new Map();
    
    strengthWorkouts.forEach(workout => {
      workout.exercises?.forEach(exercise => {
        if (exercise.weight_kg && exercise.name) {
          if (!exerciseProgress.has(exercise.name)) {
            exerciseProgress.set(exercise.name, []);
          }
          exerciseProgress.get(exercise.name).push({
            date: workout.completed_at!,
            weight: exercise.weight_kg,
            reps: exercise.reps,
            sets: exercise.sets
          });
        }
      });
    });

    const progressData = [];
    exerciseProgress.forEach((records, exerciseName) => {
      const sortedRecords = records.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      if (sortedRecords.length >= 2) {
        const first = sortedRecords[0];
        const latest = sortedRecords[sortedRecords.length - 1];
        const improvement = latest.weight - first.weight;
        
        if (improvement !== 0) {
          progressData.push({
            exercise: exerciseName,
            previous: first.weight,
            current: latest.weight,
            improvement: improvement > 0 ? `+${improvement}kg` : `${improvement}kg`,
            improvementValue: improvement
          });
        }
      }
    });

    return progressData
      .sort((a, b) => Math.abs(b.improvementValue) - Math.abs(a.improvementValue))
      .slice(0, 5);
  };

  const getRunningProgress = () => {
    const runningWorkouts = workouts.filter(w => 
      w.completed_at && w.workout_type === 'running'
    ).sort((a, b) => new Date(a.completed_at!).getTime() - new Date(b.completed_at!).getTime());

    const progressData = [];
    
    if (runningWorkouts.length >= 2) {
      // Total distance progress
      const totalDistance = runningWorkouts.reduce((sum, w) => {
        const runningExercise = w.exercises?.find(e => e.name === 'Running');
        return sum + (runningExercise?.distance_km || 0);
      }, 0);
      
      // Average pace improvement
      const paceTimes = runningWorkouts
        .map(w => {
          const runningExercise = w.exercises?.find(e => e.name === 'Running');
          if (runningExercise?.distance_km && w.duration_minutes) {
            return w.duration_minutes / runningExercise.distance_km; // minutes per km
          }
          return null;
        })
        .filter(Boolean);
      
      if (paceTimes.length >= 2) {
        const firstPace = paceTimes[0];
        const latestPace = paceTimes[paceTimes.length - 1];
        const improvement = firstPace - latestPace; // negative means faster
        
        progressData.push({
          metric: 'Average Pace',
          previous: `${Math.floor(firstPace!)}:${String(Math.round((firstPace! % 1) * 60)).padStart(2, '0')}/km`,
          current: `${Math.floor(latestPace!)}:${String(Math.round((latestPace! % 1) * 60)).padStart(2, '0')}/km`,
          improvement: improvement > 0 ? `-${Math.floor(Math.abs(improvement))}:${String(Math.round((Math.abs(improvement) % 1) * 60)).padStart(2, '0')}/km` : `+${Math.floor(improvement)}:${String(Math.round((Math.abs(improvement) % 1) * 60)).padStart(2, '0')}/km`
        });
      }

      // Longest distance
      const distances = runningWorkouts.map(w => {
        const runningExercise = w.exercises?.find(e => e.name === 'Running');
        return runningExercise?.distance_km || 0;
      });
      
      const firstDistance = distances[0];
      const longestDistance = Math.max(...distances);
      
      if (longestDistance > firstDistance) {
        progressData.push({
          metric: 'Longest Run',
          previous: `${firstDistance}km`,
          current: `${longestDistance}km`,
          improvement: `+${(longestDistance - firstDistance).toFixed(1)}km`
        });
      }

      // Total running distance
      progressData.push({
        metric: 'Total Distance',
        previous: '0km',
        current: `${totalDistance.toFixed(1)}km`,
        improvement: `+${totalDistance.toFixed(1)}km`
      });
    }

    return progressData;
  };

  const updateWorkout = async (
    workoutId: string,
    updates: Partial<Workout>,
    options?: { skipRefresh?: boolean }
  ) => {
    if (!user) return { error: 'No user found' };

    try {
      const { error } = await supabase
        .from('workouts')
        .update(updates)
        .eq('id', workoutId)
        .eq('user_id', user.id);

      if (error) throw error;

      if (!options?.skipRefresh) {
        await fetchWorkouts(); // Refresh workouts
      }

      return { error: null };
    } catch (error) {
      console.error('Error updating workout:', error);
      return { error };
    }
  };

  const updateExercise = async (
    exerciseId: string,
    updates: Partial<Exercise>,
    options?: { skipRefresh?: boolean }
  ) => {
    if (!user) return { error: 'No user found' };

    try {
      const { error } = await supabase
        .from('exercises')
        .update(updates)
        .eq('id', exerciseId);

      if (error) throw error;

      if (!options?.skipRefresh) {
        await fetchWorkouts(); // Refresh workouts
      }

      return { error: null };
    } catch (error) {
      console.error('Error updating exercise:', error);
      return { error };
    }
  };

  const deleteExercise = async (exerciseId: string, options?: { skipRefresh?: boolean }) => {
    if (!user) return { error: 'No user found' };

    try {
      const { error } = await supabase
        .from('exercises')
        .delete()
        .eq('id', exerciseId);

      if (error) throw error;

      if (!options?.skipRefresh) {
        await fetchWorkouts(); // Refresh workouts
      }

      return { error: null };
    } catch (error) {
      console.error('Error deleting exercise:', error);
      return { error };
    }
  };

  const deleteWorkout = async (workoutId: string) => {
    if (!user) return { error: 'No user found' };

    try {
      // First delete all exercises associated with the workout
      const { error: exerciseError } = await supabase
        .from('exercises')
        .delete()
        .eq('workout_id', workoutId);

      if (exerciseError) throw exerciseError;

      // Then delete the workout itself
      const { error } = await supabase
        .from('workouts')
        .delete()
        .eq('id', workoutId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      await fetchWorkouts(); // Refresh workouts
      return { error: null };
    } catch (error) {
      console.error('Error deleting workout:', error);
      return { error };
    }
  };

  return {
    workouts,
    loading,
    createWorkout,
    updateWorkout,
    deleteWorkout,
    addExerciseToWorkout,
    updateExercise,
    deleteExercise,
    completeWorkout,
    getRecentWorkouts,
    getWorkoutStats,
    getStrengthProgress,
    getRunningProgress,
    refetch: fetchWorkouts
  };
};